/**
 * Get the display image for a pack or product
 * Priority: mockups > product images > pack image > placeholder
 * Only uses mockups with show_image_on_store = true/1
 * Prioritizes mockups with store_primary_image = true/1 as the default first image
 */
export const getPackDisplayImage = (pack, fallbackImage) => {
  const defaultPlaceholder =
    fallbackImage ||
    "https://content.swageazy.com/blogs/Vector-(1)_1742630924118.png";

  // Step 1: Collect all mockups from all products in the pack
  const allMockups = [];
  if (pack?.products && Array.isArray(pack.products)) {
    pack.products.forEach((product) => {
      if (product?.mockups && Array.isArray(product.mockups)) {
        product.mockups.forEach((mockup) => {
          // Only include mockups where show_image_on_store is true/1
          const showOnStore =
            mockup.show_image_on_store === 1 ||
            mockup.show_image_on_store === true;
          if (showOnStore && mockup?.image_url) {
            allMockups.push(mockup);
          }
        });
      }
    });
  }

  // If mockups exist, use them (prioritize primary)
  if (allMockups.length > 0) {
    // Find mockup with store_primary_image = true/1 (primary image)
    const primaryMockup = allMockups.find(
      (mockup) =>
        mockup.store_primary_image === 1 || mockup.store_primary_image === true
    );

    if (primaryMockup?.image_url) {
      return primaryMockup.image_url;
    }

    // If no primary image, use first mockup
    if (allMockups[0]?.image_url) {
      return allMockups[0].image_url;
    }
  }

  // Step 2: Fallback to product images (image_urls)
  if (pack?.products && Array.isArray(pack.products)) {
    for (const product of pack.products) {
      if (product?.image_urls && Array.isArray(product.image_urls)) {
        const productImage = product.image_urls.find(
          (url) => url && url.trim()
        );
        if (productImage) {
          return productImage;
        }
      }
    }
  }

  // Step 3: Fallback to pack image
  if (pack?.pack_image_url && pack.pack_image_url.trim()) {
    return pack.pack_image_url;
  }

  // Step 4: Final fallback to placeholder
  return defaultPlaceholder;
};

/**
 * Get all images for a pack or product for gallery display
 * Priority: mockups > product images > pack image > placeholder
 * Only returns mockups with show_image_on_store = true/1
 * Mockups with store_primary_image = true/1 are placed first
 * Returns an array of image URLs
 */
export const getPackDisplayImages = (pack, fallbackImage) => {
  const defaultPlaceholder =
    fallbackImage ||
    "https://content.swageazy.com/blogs/Vector-(1)_1742630924118.png";

  const images = [];

  // Step 1: Collect all mockups from all products in the pack
  const allMockups = [];
  if (pack?.products && Array.isArray(pack.products)) {
    pack.products.forEach((product) => {
      if (product?.mockups && Array.isArray(product.mockups)) {
        product.mockups.forEach((mockup) => {
          // Only include mockups where show_image_on_store is true/1
          const showOnStore =
            mockup.show_image_on_store === 1 ||
            mockup.show_image_on_store === true;
          if (showOnStore && mockup?.image_url) {
            allMockups.push(mockup);
          }
        });
      }
    });
  }

  // If mockups exist, use them (prioritize primary)
  if (allMockups.length > 0) {
    // Separate primary and non-primary mockups
    const primaryMockups = allMockups.filter(
      (mockup) =>
        mockup.store_primary_image === 1 || mockup.store_primary_image === true
    );
    const nonPrimaryMockups = allMockups.filter(
      (mockup) =>
        !(mockup.store_primary_image === 1 || mockup.store_primary_image === true)
    );

    // Build image array: primary first, then others
    primaryMockups.forEach((mockup) => {
      if (mockup.image_url) {
        images.push(mockup.image_url);
      }
    });
    nonPrimaryMockups.forEach((mockup) => {
      if (mockup.image_url) {
        images.push(mockup.image_url);
      }
    });
  }

  // Step 2: If no mockups, fallback to product images (image_urls)
  if (images.length === 0 && pack?.products && Array.isArray(pack.products)) {
    pack.products.forEach((product) => {
      if (product?.image_urls && Array.isArray(product.image_urls)) {
        product.image_urls.forEach((url) => {
          if (url && url.trim()) {
            images.push(url);
          }
        });
      }
    });
  }

  // Step 3: If still no images, fallback to pack image
  if (images.length === 0 && pack?.pack_image_url && pack.pack_image_url.trim()) {
    images.push(pack.pack_image_url);
  }

  // Remove duplicates and filter out empty strings
  const uniqueImages = [...new Set(images.filter((img) => img && img.trim()))];

  // Step 4: Final fallback to placeholder
  return uniqueImages.length > 0 ? uniqueImages : [defaultPlaceholder];
};
